import {
  createContext,
  useCallback,
  useContext,
  useSyncExternalStore,
} from 'react'

/**
 * What has been typed into a search box but not yet searched (Enter). Lists filter on
 * it as plain text while the person types; Enter runs the full search instead: the
 * query syntax, the URL and, when on, the content search.
 *
 * Keyed like the search URL param (by paramPrefix). The page has one store; each
 * NavigateProvider (e.g. a modal with its own filters) makes its own, so typing in a
 * modal does not filter the page behind it.
 */
export type QuickSearchStore = {
  get: (key: string) => string | null
  set: (key: string, value: string | null) => void
  subscribe: (onChange: () => void) => () => void
}

export const createQuickSearchStore = (): QuickSearchStore => {
  const values = new Map<string, string>()
  const listeners = new Set<() => void>()
  return {
    get: (key) => values.get(key) ?? null,
    set: (key, value) => {
      if ((values.get(key) ?? null) === value) return
      value === null ? values.delete(key) : values.set(key, value)
      listeners.forEach((listener) => listener())
    },
    subscribe: (onChange) => {
      listeners.add(onChange)
      return () => {
        listeners.delete(onChange)
      }
    },
  }
}

export const QuickSearchContext = createContext<QuickSearchStore>(
  createQuickSearchStore(),
)

const keyFor = (paramPrefix?: string): string => paramPrefix ?? ''

// The text typed and not yet searched, or null when there is none.
export const useQuickSearchQuery = (paramPrefix?: string): string | null => {
  const store = useContext(QuickSearchContext)
  const key = keyFor(paramPrefix)
  return useSyncExternalStore(store.subscribe, () => store.get(key))
}

export const useSetQuickSearchQuery = (
  paramPrefix?: string,
): ((value: string | null) => void) => {
  const store = useContext(QuickSearchContext)
  const key = keyFor(paramPrefix)
  return useCallback((value) => store.set(key, value), [store, key])
}

// Typed text as a plain phrase: query syntax is for the full search, so quotes are
// dropped rather than read.
export const toQuickSearchText = (typed: string): string =>
  typed.replace(/"/g, '').trim()
